import React, { useState } from 'react';
import { useUser } from '../../../Context/useUser';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import API from '../../../Utils/api';
import {
    downloadBlobResponse,
    getBackupDownloadErrorMessage,
    MIN_BACKUP_FILE_SIZE
} from '../../../Utils/backupDownload';
import styles from './DashboardPage.module.css';
import Avatar from 'react-avatar';
import { MdOutlineSecurity, MdDashboard, MdBackup } from 'react-icons/md';
import { FaUsers, FaComments } from 'react-icons/fa';
import OverviewStatCard from './OverviewStatCard';
import BackupRestoreGuide from './BackupRestoreGuide';

export default function DashboardPage() {
    const { user } = useUser();
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('overview');
    const [isDownloadingBackup, setIsDownloadingBackup] = useState(false);

    const {
        register: registerPassword,
        handleSubmit: handlePasswordSubmit,
        reset: resetPasswordForm,
        formState: { errors: passwordErrors, isSubmitting }
    } = useForm();

    const onPasswordChange = async ({ currentPassword, newPassword, confirmPassword }) => {
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match.');
            return;
        }
        try {
            await API.updatePassword({
                userId: user._id,
                oldPassword: currentPassword,
                newPassword
            });
            toast.success('Password updated successfully.');
            resetPasswordForm();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Failed to update password.');
        }
    };

    const handleDownloadBackup = async () => {
        setIsDownloadingBackup(true);
        try {
            const response = await API.downloadBackup();

            if (!response.data || response.data.size < MIN_BACKUP_FILE_SIZE) {
                toast.error('Backup file appears empty. Please try again or check the API logs.');
                return;
            }

            const fallbackFilename = `hydromastery-backup-${new Date().toISOString().slice(0, 10)}.zip`;
            downloadBlobResponse(response, fallbackFilename);
            toast.success('Database backup downloaded.');
        } catch (error) {
            toast.error(await getBackupDownloadErrorMessage(error));
        } finally {
            setIsDownloadingBackup(false);
        }
    };

    return (
        <div className={styles.dashboardContainer}>
            <div className={styles.header + ' island'}>
                <div className={styles.profileSection}>
                    <Avatar
                        name={`${user?.firstName} ${user?.lastName}`}
                        size='80'
                        round={true}
                        className={styles.avatar}
                    />
                    <div className={styles.profileInfo}>
                        <h2>
                            {user?.firstName} {user?.lastName}
                        </h2>
                        <p className={styles.email}>{user?.email}</p>
                        <span className={styles.roleBadge}>Administrator</span>
                    </div>
                </div>
            </div>

            <div className={styles.tabContainer + ' island'}>
                <button
                    className={`${styles.toggleBtn} ${viewMode === 'overview' ? styles.active : ''}`}
                    onClick={() => setViewMode('overview')}
                    title='Dashboard Overview'
                >
                    <MdDashboard size={20} />
                    <span>Overview</span>
                </button>
                <button
                    className={`${styles.toggleBtn} ${viewMode === 'security' ? styles.active : ''}`}
                    onClick={() => setViewMode('security')}
                    title='Security Settings'
                >
                    <MdOutlineSecurity size={20} />
                    <span>Security</span>
                </button>
            </div>

            {viewMode === 'overview' && (
                <div className={styles.overviewContent}>
                    <div className={styles.statsGrid}>
                        <OverviewStatCard
                            icon={FaUsers}
                            title='User Management'
                            description='Search and manage all users in the system'
                            actionLabel='Go to Users'
                            onAction={() => navigate('/users')}
                        />
                        <OverviewStatCard
                            icon={FaComments}
                            title='Feedback Inspection'
                            description='Review and manage user feedback'
                            actionLabel='View Feedback'
                            onAction={() => navigate('/feedback-inspection')}
                        />
                        <OverviewStatCard
                            icon={MdOutlineSecurity}
                            title='Security'
                            description='Manage your password and account security'
                            actionLabel='Security Settings'
                            onAction={() => setViewMode('security')}
                        />
                    </div>

                    <section className={`${styles.backupSection} island`} aria-labelledby='backup-section-title'>
                        <div className={styles.statIcon} aria-hidden='true'>
                            <MdBackup />
                        </div>
                        <div className={styles.statInfo}>
                            <h3 id='backup-section-title'>Database Backup</h3>
                            <p className={styles.statCardDescription}>
                                Download a single .zip file containing all app data. Keep it somewhere
                                safe — you only need this one file to restore later.
                            </p>
                            <button
                                type='button'
                                onClick={handleDownloadBackup}
                                className={`btn btn-primary btn-sm ${styles.cardAction}`}
                                disabled={isDownloadingBackup}
                            >
                                {isDownloadingBackup ? 'Preparing backup...' : 'Download Backup'}
                            </button>
                            <BackupRestoreGuide />
                        </div>
                    </section>
                </div>
            )}

            {viewMode === 'security' && (
                <div className={styles.securityPage}>
                    <div className={styles.changePasswordArea + ' island'}>
                        <div className={styles.sectionHeader}>
                            <h3>Change Password</h3>
                            <p>Update your password to keep your account secure</p>
                        </div>
                        <form
                            className={styles.changePasswordContent + ' form-group'}
                            onSubmit={handlePasswordSubmit(onPasswordChange)}
                        >
                            <div className={styles.changePasswordField}>
                                <label htmlFor='currentPassword'>Current Password</label>
                                <input
                                    type='password'
                                    className='form-control'
                                    id='currentPassword'
                                    placeholder='Enter current password'
                                    {...registerPassword('currentPassword', {
                                        required: true
                                    })}
                                />
                                {passwordErrors.currentPassword && (
                                    <span className={styles.error}>
                                        Current password is required
                                    </span>
                                )}
                            </div>
                            <div className={styles.changePasswordField}>
                                <label htmlFor='newPassword'>New Password</label>
                                <input
                                    type='password'
                                    className='form-control'
                                    id='newPassword'
                                    placeholder='Enter new password'
                                    {...registerPassword('newPassword', {
                                        required: true,
                                        minLength: 6
                                    })}
                                />
                                {passwordErrors.newPassword && (
                                    <span className={styles.error}>
                                        New password is required (min 6 chars)
                                    </span>
                                )}
                            </div>
                            <div className={styles.changePasswordField}>
                                <label htmlFor='confirmPassword'>Confirm New Password</label>
                                <input
                                    type='password'
                                    className='form-control'
                                    id='confirmPassword'
                                    placeholder='Confirm new password'
                                    {...registerPassword('confirmPassword', {
                                        required: true
                                    })}
                                />
                                {passwordErrors.confirmPassword && (
                                    <span className={styles.error}>
                                        Please confirm your new password
                                    </span>
                                )}
                            </div>
                            <button
                                className={`${styles.changePasswordFormButton} btn btn-primary`}
                                type='submit'
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Updating...' : 'Update Password'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
